'use client';

import { useEffect, useState } from 'react';

/**
 * The trailing edge of `value`: whatever it settled on `ms` after it last
 * changed.
 *
 * Every caller here uses it for the same reason — an autocomplete or an
 * availability check that must not fire one rate-limited network call per
 * keystroke. Debounce the value the QUERY takes, not the raw field: pass
 * `value.trim()` / `finalizeSlugInput(...)` in, so the transform and the
 * delay stay one expression instead of a hand-rolled effect per call site.
 *
 * ⚠️ Pass the delay explicitly at every call site. The default exists so the
 * signature reads, not so a site can inherit a number — one of the four
 * callers deliberately waits 400ms, and normalising it would make a
 * live-checked slug feel twitchy.
 *
 * NOT for the URL-param debouncers in `admin/{businesses,leads,tasks}/page.tsx`
 * — those carry an extra equality guard before writing history, which is a
 * different job from delaying a value.
 */
export function useDebouncedValue<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);

  return debounced;
}
